/**
 * 已连接钱包检测工具
 * @fileoverview 检测当前已连接的钱包类型和版本号
 * @version 1.1.0
 */

/**
 * 检测当前连接的钱包
 * @param {Object} provider - 连接成功的钱包provider对象
 * @returns {Object} 检测结果对象
 */
function detectConnectedWallet(provider) {
  if (!provider) {
    console.warn('检测失败：未提供有效的钱包provider对象');
    return getEmptyResult('未提供钱包对象');
  }

  try {
    // 1. 首选：如果是TronLink（波场网络）
    if (isTronLink(provider)) {
      return buildResult('TronLink', getTronLinkVersion(provider), 'tron');
    }

    // 2. 检查以太坊系钱包的特定标识（按您指定的顺序）
    const ethWalletChecks = [
      { name: 'TokenPocket', check: () => provider.isTokenPocket },
      { name: 'imToken', check: () => provider.isImToken },
      { name: 'OneKey', check: () => provider.isOneKey },
      { name: 'Bitpie', check: () => provider.isBitpie },
      // 常见备选钱包，可按需移除
      { name: 'MetaMask', check: () => provider.isMetaMask },
      { name: 'Coinbase Wallet', check: () => provider.isCoinbaseWallet },
      { name: 'Trust Wallet', check: () => provider.isTrust },
    ];

    for (const wallet of ethWalletChecks) {
      if (wallet.check()) {
        return buildResult(wallet.name, getEthWalletVersion(provider, wallet.name), 'ethereum');
      }
    }

    // 3. 通用以太坊Provider（无特定标识）
    if (typeof provider.request === 'function') {
      return buildResult('通用以太坊钱包', getEthWalletVersion(provider, '通用'), 'ethereum');
    }

    // 4. 如果以上都不是，尝试从对象本身推断
    return inferWalletFromProvider(provider);

  } catch (error) {
    console.error('检测过程中发生错误:', error);
    return buildResult('检测错误', '未知', 'error', error.message);
  }
}

/**
 * 判断是否为TronLink
 */
function isTronLink(provider) {
  return (
    provider.isTronLink === true ||
    (typeof provider.tronWeb !== 'undefined') ||
    (typeof provider.request === 'function' && 
     typeof provider.tronLink !== 'undefined') ||
    provider === window.tronLink ||
    provider === window.tronWeb
  );
}

/**
 * 获取TronLink版本号
 */
function getTronLinkVersion(provider) {
  // 优先级1: provider自身的version属性
  if (provider.version) return provider.version;
  
  // 优先级2: tronLink或tronWeb子对象中的version
  if (provider.tronLink && provider.tronLink.version) return provider.tronLink.version;
  if (provider.tronWeb && provider.tronWeb.version) return provider.tronWeb.version;
  
  // 优先级3: window全局对象中的version
  if (window.tronLink && window.tronLink.version) return window.tronLink.version;
  if (window.tronWeb && window.tronWeb.version) return window.tronWeb.version;
  
  // 优先级4: 从User-Agent提取
  const ua = navigator.userAgent;
  const tronMatch = ua.match(/TronLink[\/\s]([0-9.]+)/i);
  if (tronMatch && tronMatch[1]) return tronMatch[1];
  
  return '未知';
}

/**
 * 获取以太坊系钱包版本号
 */
function getEthWalletVersion(provider, walletName) {
  // 优先级1: provider自身的version属性
  if (provider.version) return provider.version;
  
  // 优先级2: 钱包特定的版本属性
  if (walletName === 'TokenPocket' && provider.tpVersion) return provider.tpVersion;
  if (walletName === 'OneKey' && provider.onekeyVersion) return provider.onekeyVersion;
  
  // 优先级3: 从User-Agent提取
  const ua = navigator.userAgent;
  const walletPatterns = [
    { name: 'TokenPocket', pattern: /TokenPocket[\/\s]([0-9.]+)/i },
    { name: 'imToken', pattern: /imToken[\/\s]([0-9.]+)/i },
    { name: 'OneKey', pattern: /OneKey[\/\s]([0-9.]+)/i },
    { name: 'Bitpie', pattern: /Bitpie[\/\s]([0-9.]+)/i },
    { name: 'MetaMask', pattern: /MetaMask[\/\s]([0-9.]+)/i },
  ];
  
  for (const item of walletPatterns) {
    if (item.name === walletName) {
      const match = ua.match(item.pattern);
      if (match && match[1]) return match[1];
    }
  }
  
  // 通用匹配模式
  const genericMatch = ua.match(/(Wallet|Ethereum)[\/\s]([0-9.]+)/i);
  if (genericMatch && genericMatch[2]) return genericMatch[2];
  
  return '未知';
}

/**
 * 通过provider对象推断钱包类型（备用方法）
 */
function inferWalletFromProvider(provider) {
  // 检查provider是否来自window特定对象
  if (provider === window.tokenpocket) {
    const version = window.tokenpocket.version || '未知';
    return buildResult('TokenPocket', version, 'ethereum');
  }
  if (provider === window.imToken) {
    const version = window.imToken.version || '未知';
    return buildResult('imToken', version, 'ethereum');
  }
  if (provider === window.$onekey) {
    const version = window.$onekey.version || '未知';
    return buildResult('OneKey', version, 'ethereum');
  }
  if (provider === window.bitpie) {
    const version = window.bitpie.version || '未知';
    return buildResult('Bitpie', version, 'ethereum');
  }
  
  return buildResult('未知钱包', '未知', 'unknown');
}

/**
 * 构建标准化的返回结果
 */
function buildResult(name, version, type, error = null) {
  return {
    name: name,
    version: version,
    type: type, // 'ethereum', 'tron', 'unknown', 'error'
    isSupported: ['TronLink', 'TokenPocket', 'imToken', 'OneKey', 'Bitpie'].includes(name),
    timestamp: new Date().toISOString(),
    ...(error && { error: error })
  };
}

/**
 * 获取空结果
 */
function getEmptyResult(reason) {
  return buildResult('未检测到', 'N/A', 'none', reason);
}

// 导出函数
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { detectConnectedWallet };
}
if (typeof window !== 'undefined') {
  window.detectConnectedWallet = detectConnectedWallet;
}
export { detectConnectedWallet };